import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdCloudUpload, MdClose, MdAutoFixHigh, MdCheckCircle } from 'react-icons/md';

interface TearSheetPanelProps {
  uploadedFile: File | null;
  uploadedPreview: string | null;
  dimensions: string;
  isGenerating: boolean;
  generatedImageUrl: string | null;
  error: string | null;
  onFileSelect: (file: File) => void;
  onFileRemove: () => void;
  onDimensionsChange: (value: string) => void;
  onGenerate: () => void;
}

const TearSheetPanel: React.FC<TearSheetPanelProps> = ({
  uploadedFile,
  uploadedPreview,
  dimensions,
  isGenerating,
  generatedImageUrl,
  error,
  onFileSelect,
  onFileRemove,
  onDimensionsChange,
  onGenerate,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) onFileSelect(file);
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = () => setIsDragOver(false);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileSelect(file);
  };

  const canGenerate = uploadedFile && dimensions.trim().length > 0 && !isGenerating;

  return (
    <div className="h-full flex flex-col overflow-y-auto">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-white/[0.05]">
        <h2 className="text-sm font-bold text-white tracking-tight">Workspace Controls</h2>
        <p className="text-[10px] text-gray-500 mt-0.5 leading-relaxed">
          Configure your furniture specification sheet.
        </p>
      </div>

      {/* Product Image Section */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Product Image</h3>
          {uploadedPreview && <MdCheckCircle size={12} className="text-emerald-400" />}
        </div>

        {!uploadedPreview ? (
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`relative rounded-xl border-[1.5px] border-dashed transition-all duration-200 cursor-pointer h-[140px] ${
              isDragOver
                ? 'border-[#EE4423]/50 bg-[#EE4423]/5'
                : 'border-white/10 hover:border-white/20 hover:bg-white/[0.02]'
            }`}
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="w-9 h-9 rounded-xl bg-white/[0.05] flex items-center justify-center mb-2">
                <MdCloudUpload size={18} className={`transition-colors ${isDragOver ? 'text-[#EE4423]' : 'text-gray-500'}`} />
              </div>
              <p className="text-[11px] font-medium text-gray-400">
                {isDragOver ? 'Drop here' : 'Click or drag to upload'}
              </p>
              <p className="text-[9px] text-gray-600 mt-0.5">PNG, JPG, JPEG</p>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileInput} className="hidden" />
          </div>
        ) : (
          <div className="relative rounded-xl overflow-hidden group h-[140px] bg-gray-800/30">
            <img src={uploadedPreview} alt="Uploaded" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-200 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-all flex items-center gap-1.5">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-2.5 py-1 rounded-md bg-white/20 backdrop-blur-sm text-white text-[10px] font-medium hover:bg-white/30 transition-colors cursor-pointer"
                >
                  Replace
                </button>
                <button
                  onClick={onFileRemove}
                  className="w-7 h-7 rounded-md bg-red-500/60 hover:bg-red-500/80 flex items-center justify-center cursor-pointer transition-colors"
                >
                  <MdClose size={14} className="text-white" />
                </button>
              </div>
            </div>
            <div className="absolute bottom-1.5 left-1.5 right-1.5">
              <div className="px-2 py-0.5 rounded-md bg-black/60 backdrop-blur-md">
                <p className="text-[9px] text-white/70 truncate">{uploadedFile?.name}</p>
              </div>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileInput} className="hidden" />
          </div>
        )}
      </div>

      {/* Dimensions Section */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Dimensions</h3>
          {dimensions.trim().length > 0 && <MdCheckCircle size={12} className="text-emerald-400" />}
        </div>
        <textarea
          value={dimensions}
          onChange={(e) => onDimensionsChange(e.target.value)}
          placeholder='29" H x 32" W x 35" D'
          rows={6}
          className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-white placeholder-gray-600 resize-none focus:outline-none focus:border-[#EE4423]/40 focus:ring-1 focus:ring-[#EE4423]/20 transition-all font-mono leading-relaxed"
        />
        <p className="text-[9px] text-gray-600 mt-1">e.g. 32"W x 29"H x 35"D</p>
      </div>

      {/* Error */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="px-4"
          >
            <div className="px-2.5 py-2 rounded-lg bg-red-500/10 border border-red-500/15">
              <p className="text-[10px] text-red-400">{error}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex-1" />

      {/* Generate Button */}
      <div className="px-4 py-4 border-t border-white/[0.05]">
        <motion.button
          whileHover={{ scale: canGenerate ? 1.01 : 1 }}
          whileTap={{ scale: canGenerate ? 0.98 : 1 }}
          onClick={onGenerate}
          disabled={!canGenerate}
          className={`w-full py-2.5 rounded-lg font-semibold text-xs flex items-center justify-center gap-2 transition-all duration-200 cursor-pointer ${
            canGenerate
              ? 'bg-gradient-to-r from-[#EE4423] to-[#d63d20] text-white shadow-md shadow-[#EE4423]/15 hover:shadow-[#EE4423]/25'
              : 'bg-white/[0.04] text-gray-600 cursor-not-allowed border border-white/[0.06]'
          }`}
        >
          {isGenerating ? (
            <>
              <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              <span>Generating...</span>
            </>
          ) : (
            <>
              <MdAutoFixHigh size={15} />
              {generatedImageUrl ? 'Regenerate' : 'Generate Tear Sheet'}
            </>
          )}
        </motion.button>
      </div>
    </div>
  );
};

export default TearSheetPanel;
